/**
 * GenerationContext - Backward-Compatible Facade
 *
 * This file now serves as a facade/adapter for the Zustand generationStore.
 * All consumers continue to work without changes while using Zustand under the hood.
 *
 * MIGRATION STATUS: Phase 4 - Facade layer (backward compatible)
 * - All exports use Zustand generationStore
 * - GenerationProvider is a passthrough (no provider needed with Zustand)
 * - 4 consumers continue working without changes
 * - Navigation callback registration kept in facade (ref-based, not state)
 *
 * Original implementation: React Context with background chat state (95 lines)
 * New implementation: Thin facade wrapping Zustand (75 lines - 21% reduction)
 */

import { useCallback, useMemo } from 'react';
import {
  useIsGenerating,
  useIsComplete,
  useBackgroundChats,
  useCompletedChats,
  useIsAnalyzerStreaming,
  useActiveAnalyzerChat,
  useActiveScriptGeneration,
  useGenerationActions
} from '../../store/generation/generationStore';

// Global ref for navigation callback (doesn't need to be in Zustand state)
const navigationAttemptCallbackRef = { current: null };

/**
 * GenerationProvider - Backward-compatible wrapper
 *
 * This is now a passthrough component. The Zustand generationStore handles all state.
 */
export const GenerationProvider = ({ children }) => {
  // No provider needed - Zustand store is global
  // Just render children directly
  return children;
};

/**
 * useGeneration - Backward-compatible hook
 *
 * Returns the same API as the previous React Context implementation.
 * Consumers can continue using this without any changes.
 */
export const useGeneration = () => {
  // Subscribe to Zustand store
  const isGenerating = useIsGenerating();
  const isComplete = useIsComplete();
  const backgroundChats = useBackgroundChats();
  const completedChats = useCompletedChats();
  const isAnalyzerStreaming = useIsAnalyzerStreaming();
  const activeAnalyzerChat = useActiveAnalyzerChat();
  const activeScriptGeneration = useActiveScriptGeneration();

  const {
    setIsGenerating,
    setIsComplete,
    addBackgroundChat,
    removeBackgroundChat,
    getBackgroundChat,
    updateBackgroundChatContent,
    markChatCompleted,
    clearCompletedChat,
    setIsAnalyzerStreaming,
    setActiveAnalyzerChat,
    setActiveScriptGeneration,
    clearActiveScriptGeneration
  } = useGenerationActions();

  // Navigation callback registration (ref-based, not state)
  const registerNavigationAttemptCallback = useCallback((callback) => {
    navigationAttemptCallbackRef.current = callback;
    return () => {
      navigationAttemptCallbackRef.current = null;
    };
  }, []);

  const triggerNavigationAttempt = useCallback((destination) => {
    if (navigationAttemptCallbackRef.current) {
      return navigationAttemptCallbackRef.current(destination);
    }
    return false;
  }, []);

  // Return the same API as before
  return useMemo(() => ({
    isGenerating,
    setIsGenerating,
    isComplete,
    setIsComplete,
    backgroundChats,
    completedChats,
    addBackgroundChat,
    removeBackgroundChat,
    getBackgroundChat,
    updateBackgroundChatContent,
    markChatCompleted,
    clearCompletedChat,
    isAnalyzerStreaming,
    setIsAnalyzerStreaming,
    activeAnalyzerChat,
    setActiveAnalyzerChat,
    activeScriptGeneration,
    setActiveScriptGeneration,
    clearActiveScriptGeneration,
    registerNavigationAttemptCallback,
    triggerNavigationAttempt
  }), [
    isGenerating,
    setIsGenerating,
    isComplete,
    setIsComplete,
    backgroundChats,
    completedChats,
    addBackgroundChat,
    removeBackgroundChat,
    getBackgroundChat,
    updateBackgroundChatContent,
    markChatCompleted,
    clearCompletedChat,
    isAnalyzerStreaming,
    setIsAnalyzerStreaming,
    activeAnalyzerChat,
    setActiveAnalyzerChat,
    activeScriptGeneration,
    setActiveScriptGeneration,
    clearActiveScriptGeneration,
    registerNavigationAttemptCallback,
    triggerNavigationAttempt
  ]);
};

/**
 * Direct Zustand exports for new code
 *
 * New code can import these directly for better performance
 * (granular subscriptions - only re-render when specific fields change)
 */
export {
  useIsGenerating,
  useIsComplete,
  useBackgroundChats,
  useCompletedChats,
  useIsAnalyzerStreaming,
  useActiveAnalyzerChat,
  useActiveScriptGeneration,
  useGenerationActions
} from '../../store/generation/generationStore';
